import React from 'react';

import { VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { MetadataEditModal } from '@/shared/ui/MetadataEditModal';

import type { TagItem } from '@/entities/tags';

import { useAssetTagsSelect } from '@/features/asset-detail/model/useAssetTagsSelect';
import { useAssetTermSelect } from '@/features/asset-detail/model/useAssetTermSelect';

import styles from './AssetDetailSidebar.module.css';

type AssetDetailSidebarProps = {
  entityType: string;
  entityId: string;
  fqn: string;
  classificationTags: TagItem[];
  classificationTagsDisplay: TagItem[];
  glossaryTerms: TagItem[];
  glossaryTermsDisplay: TagItem[];
  onTermClick: (termFqn: string) => void;
};

export const AssetDetailSidebar: React.FC<AssetDetailSidebarProps> = ({
  entityType,
  entityId,
  fqn,
  classificationTags,
  classificationTagsDisplay,
  glossaryTerms,
  glossaryTermsDisplay,
  onTermClick,
}) => {
  const {
    tagsTreeData,
    handleSaveTags,
    renderTagBadgeList,
    isUpdating: isUpdatingTags,
    isOpenEditTagModal,
    setIsOpenEditTagModal,
    tagTreeSelectValue,
  } = useAssetTagsSelect({
    entityType,
    entityId,
    fqn,
    classificationTags,
    classificationTagsDisplay,
  });

  const renderTagSection = (
    <div className={styles.section}>
      <div className={styles.titleCont}>
        <p className={styles.title}>태그</p>
        <IconButton size="small" onClick={() => setIsOpenEditTagModal(true)} tooltip="태그 편집">
          <Icon name="edit" size={16} />
        </IconButton>
      </div>
      {renderTagBadgeList()}
      <MetadataEditModal
        open={isOpenEditTagModal}
        icon={<Icon name="tag" size={12} />}
        title="태그 편집"
        firstLabel="선택된 태그"
        secondLabel="태그"
        searchValidation={{
          maxLength: 30,
          warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
        }}
        loading={isUpdatingTags}
        treeData={tagsTreeData}
        treeSelectValue={tagTreeSelectValue}
        onClose={() => setIsOpenEditTagModal(false)}
        onSubmit={handleSaveTags}
      />
    </div>
  );

  const {
    glossaryTree,
    handleSaveTerms,
    renderTermBadgeList,
    isUpdatingTerms,
    isOpenEditTermModal,
    setIsOpenEditTermModal,
    termTreeSelectValue,
  } = useAssetTermSelect({
    entityType,
    entityId,
    fqn,
    glossaryTerms,
    glossaryTermsDisplay,
    onTermClick,
  });

  const renderGlossaryTermSection = (
    <div className={styles.section}>
      <div className={styles.titleCont}>
        <p className={styles.title}>용어</p>
        <IconButton size="small" onClick={() => setIsOpenEditTermModal(true)} tooltip="용어 편집">
          <Icon name="edit" size={16} />
        </IconButton>
      </div>
      {renderTermBadgeList()}
      <MetadataEditModal
        open={isOpenEditTermModal}
        icon={<Icon name="book" size={12} />}
        title="용어 편집"
        firstLabel="선택된 용어"
        secondLabel="용어"
        searchValidation={{
          maxLength: 256,
          warningMessage: VALIDATION_MESSAGES.GLOSSARY_SEARCH_MAX_LENGTH,
        }}
        loading={isUpdatingTerms}
        treeData={glossaryTree}
        treeSelectValue={termTreeSelectValue}
        onClose={() => setIsOpenEditTermModal(false)}
        onSubmit={handleSaveTerms}
      />
    </div>
  );

  return (
    <div className={styles.sidebar}>
      {renderTagSection}
      {renderGlossaryTermSection}
    </div>
  );
};
